import {inject} from '@angular/core';
import {CanActivateFn, Router} from '@angular/router';
import {StorageKeys} from '@app/core/constants/storage-keys';
import {LocalStorageService} from '@app/core/services/local-storage.service';
import {ECustomerOrganizationStatus, ECustomerStatus} from '@app/shared/enums/header.enum';
import {ICustomerProfileState, IHeaderState} from '@app/shared/models/header.model';
import {HeaderService} from '@app/shared/services/header.service';
import {getCustomerProfileSuccess} from '@app/shared/state/header/header.action';
import {Store} from '@ngrx/store';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {of} from 'rxjs';
import {catchError, map, switchMap, take, tap} from 'rxjs/operators';
import {TokenUtils} from '../utils/token.utils';

export const accountGuard: CanActivateFn = () => {
  const store = inject(Store);
  const headerService = inject(HeaderService);
  const localStorageService = inject(LocalStorageService);
  const router = inject(Router);
  const oidcSecurityService = inject(OidcSecurityService);

  return oidcSecurityService.getAccessToken().pipe(
    switchMap(accessToken => {
      if (TokenUtils.isTokenValid(accessToken ?? '')) {
        return store
          .select(state => state.headerState as IHeaderState)
          .pipe(
            take(1),
            switchMap(headerState => {
              const customerProfile$ = headerState?.customerProfileState
                ? of(headerState.customerProfileState)
                : headerService.getCustomerProfile().pipe(
                    tap(customerProfile => {
                      store.dispatch(
                        getCustomerProfileSuccess({
                          customerProfileState: customerProfile,
                        })
                      );
                    })
                  );

              return customerProfile$.pipe(
                map(customerProfile => {
                  const isFullAccount = checkIsFullAccount(customerProfile, headerState, localStorageService);
                  if (!isFullAccount) {
                    void router.navigate(['/static-pages']);
                  }
                  return isFullAccount;
                }),
                catchError(() => {
                  void router.navigate(['/static-pages']);
                  return of(false);
                })
              );
            })
          );
      } else {
        return of(false);
      }
    })
  );
};

function checkIsFullAccount(
  customerProfile: ICustomerProfileState,
  headerState: IHeaderState,
  localStorageService: LocalStorageService
) {
  const customerOrganizations = customerProfile.customerOrganizations ?? [];
  const customerStatus = customerProfile.customerStatus ?? null;

  let filteredOrganization = customerOrganizations.find(
    organization => organization.organizationId === headerState?.customerOrganization?.organizationId
  );

  if (!filteredOrganization) {
    const storedOrganizationId = localStorageService.getItem<number>(StorageKeys.selectedOrganization);
    filteredOrganization =
      customerOrganizations.find(organization => organization.organizationId === storedOrganizationId) ??
      customerOrganizations[0];

    if (
      storedOrganizationId != null &&
      filteredOrganization?.organizationId &&
      filteredOrganization.organizationId !== storedOrganizationId
    ) {
      localStorageService.setItem(StorageKeys.selectedOrganization, filteredOrganization.organizationId);
    }
  }

  const verifiedCustomer = customerStatus?.id === ECustomerStatus.Verified;
  const activeOrganization =
    filteredOrganization?.organization?.organizationStatus?.id === ECustomerOrganizationStatus.Active;
  const activeCustomerOrganization =
    filteredOrganization?.customerOrganizationStatus?.id === ECustomerOrganizationStatus.Active;

  return verifiedCustomer && activeOrganization && activeCustomerOrganization;
}
